import { memo } from "react";
import {
  IconBottle,
  IconClock,
  IconGasStation,
  IconHelmet,
  IconLockSquareRounded,
  IconMap,
  IconMapPin,
  IconPlant2,
  IconMotorbike,
  IconTelescope,
} from "@tabler/icons-react";
import { cn } from "@/lib/utils";
import type {
  ServiceCardIconKey,
  ServiceIconComponent,
  ServiceIconProps,
  ServiceMetaProps,
} from "./service.card.types";

// Map icon key to an actual icon component
const ICON_COMPONENT_MAP: Record<ServiceCardIconKey, ServiceIconComponent> = {
  helmet: IconHelmet,
  fuel: IconGasStation,
  map: IconMap,
  lock: IconLockSquareRounded,
  bottle: IconBottle,
  motorcycle: IconMotorbike,
  massage: IconPlant2,
  clock: IconClock,
  telescope: IconTelescope,
  location: IconMapPin,
};

// Render icon component based on icon key
export const ServiceIcon = ({
  icon,
  className,
  strokeWidth = 1.8,
}: ServiceIconProps) => {
  const IconComponent = ICON_COMPONENT_MAP[icon];
  return <IconComponent className={className} strokeWidth={strokeWidth} />;
};

// Render meta items
const ServiceMeta = ({ items, className }: ServiceMetaProps) => {
  if (!items?.length) {
    return null;
  }

  return (
    <div className={cn("flex min-w-0 flex-col gap-1", className)}>
      {items.map((item, index) => (
        <div
          key={`${item.icon}-${item.text}-${index}`}
          className="flex items-center gap-1.5 text-xs leading-5 text-[#898D91] md:text-[13px]"
        >
          <ServiceIcon
            icon={item.icon}
            className="h-3.5 w-3.5 shrink-0 text-[#9CA3AF] md:h-4 md:w-4"
          />
          <span className="truncate">{item.text}</span>
          {item.trailingText ? (
            <>
              <span
                aria-hidden="true"
                className="h-1 w-1 shrink-0 rounded-full bg-[#D9D9D9]"
              />
              <span className="shrink-0">{item.trailingText}</span>
            </>
          ) : null}
        </div>
      ))}
    </div>
  );
};

export default memo(ServiceMeta);
